<template>
  <div class="tbank-widget">

    <!-- Error -->
    <div v-if="phase === 'error'" class="tbank-widget__center">
      <q-icon name="error_outline" color="negative" size="36px" />
      <div class="q-mt-xs text-caption text-negative">{{ errorText }}</div>
      <q-btn flat dense no-caps color="primary" label="Повторить" class="q-mt-sm" @click="setup" />
    </div>

    <!-- ─── redirect mode ───────────────────────────────────── -->
    <template v-else-if="mode === 'redirect' && !triggerOnly">
      <q-btn
        unelevated
        no-caps
        color="yellow-8"
        text-color="black"
        :label="btnLabel"
        :loading="phase === 'loading'"
        :disable="phase === 'loading'"
        icon="credit_card"
        @click="openRedirect"
      />
    </template>

    <!-- ─── speedpay mode ───────────────────────────────────── -->
    <template v-else-if="mode === 'speedpay'">
      <div v-if="phase === 'initializing'" class="tbank-widget__center">
        <q-spinner color="primary" size="36px" />
      </div>
      <div ref="speedpayContainer" class="tbank-widget__speedpay" />
    </template>

  </div>
</template>

<script>
const INTEGRATION_SCRIPT = "https://integrationjs.t-static.ru/integration.js";
const TERMINAL_KEY = process.env.VUE_APP_TBANK_TERMINAL_KEY;

let scriptPromise = null;
function loadIntegrationScript() {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    if (window.PaymentIntegration) { resolve(); return; }
    const script = document.createElement("script");
    script.src = INTEGRATION_SCRIPT;
    script.async = true;
    script.onload = resolve;
    script.onerror = () => {
      scriptPromise = null;
      reject(new Error("Не удалось загрузить скрипт TBank"));
    };
    document.head.appendChild(script);
  });
  return scriptPromise;
}

let integrationInstance = null;
async function getIntegration() {
  if (integrationInstance) return integrationInstance;
  integrationInstance = await window.PaymentIntegration.init({
    terminalKey: TERMINAL_KEY,
    product: "eacq",
    features: { payment: {} },
  });
  return integrationInstance;
}

export default {
  name: "TBankWidget",

  props: {
    amount:      { type: Number, required: true },
    email:       { type: String, default: "" },
    payerName:   { type: String, default: "" },
    triggerOnly: { type: Boolean, default: false },
    // 'redirect'  — opens TBank payment page in a new tab
    // 'speedpay'  — renders SBP / T-Pay buttons inline
    mode: {
      type: String,
      default: "redirect",
      validator: v => ["redirect", "speedpay"].includes(v),
    },
    widgetTypes: { type: Array,  default: () => ["sbp", "tpay"] },
    btnLabel:    { type: String, default: "Оплатить картой" },
  },

  data() {
    return {
      phase: "initializing",
      errorText: "",
      speedpayInst: null,
    };
  },

  mounted() {
    this.setup();
  },

  beforeDestroy() {
    if (this.speedpayInst) {
      try { this.speedpayInst.remove(); } catch (_) {}
    }
  },

  methods: {
    async setup() {
      this.phase = "initializing";
      this.errorText = "";
      try {
        if (this.mode === "speedpay") {
          await loadIntegrationScript();
          const integration = await getIntegration();
          await this.setupSpeedpay(integration);
        } else {
          // redirect mode needs no pre-loading
          this.phase = "ready";
        }
      } catch (e) {
        this.setError(e.message || "Ошибка инициализации платёжного виджета");
      }
    },

    async setupSpeedpay(integration) {
      await this.$nextTick();
      const container = this.$refs.speedpayContainer;
      this.speedpayInst = await integration.payments.create("tbank-speedpay-main", {});
      this.speedpayInst.mount(container);
      this.speedpayInst.updateWidgetTypes(this.widgetTypes);
      this.speedpayInst.setPaymentStartCallback(() => this.fetchPaymentUrl(true));
      this.phase = "ready";
    },

    async openRedirect() {
      if (this.phase === "loading") return;
      this.phase = "loading";
      try {
        const paymentUrl = await this.fetchPaymentUrl();
        window.open(paymentUrl, "_blank");
      } catch (e) {
        this.setError(e.message || "Не удалось создать платёж");
        return;
      }
      this.phase = "ready";
    },

    buildReceipt() {
      const receipt = {
        Taxation: "usn_income",
        Items: [
          {
            Name: "Доступ к сервису ПОЧТУМ.РФ (1 год)",
            Price: this.amount,
            Quantity: 1,
            Amount: this.amount,
            Tax: "none",
            PaymentMethod: "full_payment",
            PaymentObject: "service",
            MeasurementUnit: "шт",
          },
        ],
      };
      if (this.email) receipt.Email = this.email;
      return receipt;
    },

    async fetchPaymentUrl(forWidget = false) {
      const body = { Amount: this.amount, Receipt: this.buildReceipt() };
      if (forWidget) body.Data = { connection_type: "Widget" };
      if (this.payerName) body.PayerName = this.payerName;
      const res = await this.$api.post("/api/tbank/init/", body);
      if (!res.data.Success) {
        throw new Error(res.data.Message || "Ошибка создания платежа");
      }
      return res.data.PaymentURL;
    },

    setError(text) {
      this.phase = "error";
      this.errorText = text;
      integrationInstance = null;
      scriptPromise = null;
    },
  },
};
</script>

<style lang="sass" scoped>
.tbank-widget__center
  display: flex
  flex-direction: column
  align-items: center
  padding: 8px 0

.tbank-widget__speedpay
  min-height: 48px
</style>
